import { motion } from "framer-motion";
import Image from "next/image";

import { ConversionShowcaseConnector } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseConnector";
import { ConversionShowcaseSongRow } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseSongRow";
import { ConversionShowcaseVoiceOption } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseVoiceOption";
import { WaveformBars } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/WaveformBars";
import { useConnectorRefs } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/hooks/useConnectorRefs";
import { Heading4 } from "@/components/text/Heading4";
import { SoftText } from "@/components/text/SoftText";
import {
  SHOWCASE_ARTISTS,
  SHOWCASE_SONGS,
} from "@/constants/conversionShowcase";
import { ShowcaseArtist, ShowcaseSong } from "@/types/defs/conversionShowcase";

export function ConversionShowcaseDesktop({
  selectedSong,
  setSelectedSong,
  selectedVoice,
  setSelectedVoice,
  playingInputLabel,
  handlePlayInput,
  isOutputPlaying,
  handlePlayOutput,
  displayWave,
  activeWave,
  animKey,
}: {
  selectedSong: ShowcaseSong;
  setSelectedSong: (song: ShowcaseSong) => void;
  selectedVoice: ShowcaseArtist;
  setSelectedVoice: (voice: ShowcaseArtist) => void;
  playingInputLabel: string | null;
  handlePlayInput: (song: ShowcaseSong) => void | Promise<void>;
  isOutputPlaying: boolean;
  handlePlayOutput: () => void | Promise<void>;
  displayWave: number[];
  activeWave: boolean;
  animKey: string;
}) {
  const { containerRef, fromRefs, toRefs, registerFromRef, registerToRef } =
    useConnectorRefs();

  return (
    <div className="hidden lg:flex lg:justify-center">
      <div className="flex w-fit flex-col">
        <div className="mb-8">
          <Heading4 elementType="h3">Convert vocals</Heading4>
          <SoftText elementType="p" className="mt-1">
            Transform your raw vocal recordings into any voice with
            studio-quality sound
          </SoftText>
        </div>
        <div
          ref={containerRef}
          className="relative flex flex-row items-start gap-24"
        >
          <ConversionShowcaseConnector
            fromRefs={fromRefs}
            toRefs={toRefs}
            containerRef={containerRef}
            selectedSongLabel={selectedSong.label}
            selectedVoiceId={selectedVoice.id}
            animKey={animKey}
          />

          <div className="flex w-[220px] flex-col items-start">
            <SoftText
              elementType="span"
              className="mb-5 flex h-9 w-full items-center rounded-full bg-gray-100 px-4 text-xs uppercase tracking-widest"
            >
              Input
            </SoftText>
            <div className="flex w-full flex-col gap-2">
              {SHOWCASE_SONGS.map((song) => (
                <ConversionShowcaseSongRow
                  key={song.label}
                  song={song}
                  isSelected={selectedSong.label === song.label}
                  isSongPlaying={playingInputLabel === song.label}
                  onPlay={() => {
                    void handlePlayInput(song);
                  }}
                  onSelect={() => {
                    setSelectedSong(song);
                  }}
                  registerFromRef={registerFromRef}
                  variant="desktop"
                />
              ))}
            </div>
          </div>

          <div className="flex w-[220px] flex-col items-start">
            <SoftText
              elementType="span"
              className="mb-5 flex h-9 w-full items-center rounded-full bg-gray-100 px-4 text-xs uppercase tracking-widest"
            >
              Voice
            </SoftText>
            <div className="flex w-full flex-col gap-2">
              {SHOWCASE_ARTISTS.map((artist) => (
                <ConversionShowcaseVoiceOption
                  key={artist.id}
                  artist={artist}
                  isSelected={selectedVoice.id === artist.id}
                  onSelect={() => {
                    setSelectedVoice(artist);
                  }}
                  registerToRef={registerToRef}
                  variant="desktop"
                />
              ))}
            </div>
          </div>

          <div className="flex w-[220px] flex-col items-start self-stretch">
            <SoftText
              elementType="span"
              className="mb-5 flex h-9 w-full items-center rounded-full bg-gray-100 px-4 text-xs uppercase tracking-widest"
            >
              Output
            </SoftText>
            <div className="flex w-full flex-col gap-0.5">
              <motion.div
                key={selectedVoice.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="relative h-28 w-full overflow-hidden rounded-xl"
              >
                <Image
                  src={selectedVoice.image}
                  alt={selectedVoice.name}
                  fill
                  className="object-cover"
                />
              </motion.div>
              <div className="relative flex h-16 w-full items-center justify-center gap-[3px] overflow-hidden">
                <WaveformBars
                  data={displayWave}
                  active={activeWave}
                  barWidth="w-[5px]"
                />
              </div>
              <motion.button
                type="button"
                onClick={() => {
                  void handlePlayOutput();
                }}
                className="flex h-8 w-fit items-center justify-center rounded-full bg-gray-900 px-3 text-sm font-medium text-white"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isOutputPlaying ? "Stop" : "Play"}
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
